import { getUploadPolicy, uploadToCstore } from "@/services/upload";
import { getFileMD5 } from "@common/utils";
import fileImportStore from '@/store/fileImport.store';
import { ICstoreUploadRes } from '@/types/upload';
import eventBus from '@/utils/event';
import { addFileToTransform, getSingleRecord, getBenefits, getFileRecords, cancelTransform, retryTransform } from '@/services/file_import';
import { message } from 'antd';
import { IFile, FileType, ImportStatus } from '@/types/fileImport';
import { MAX_UPLOAD_COUNT, POLLING_INTERVAL, DEFAULT_BENEFIT } from "@/constants/fileImport";
import fridayReporter from '@/utils/friday';
import { loadFFmpeg } from './utils/AudioProcessor';

/**
 * 文件导入底层服务
 * 负责文件上传、转写记录管理、状态轮询等底层操作
 */
class FileImportService {
  // 单例实例
  private static instance: FileImportService;

  // 上传相关状态
  private currUploadIndex: number = -1; // 当前上传的索引
  private uploadingCount: number = 0; // 当前正在上传的个数
  private uploadFiles: IFile[] = []; // 待上传的文件列表
  private signals: Map<string, AbortController> = new Map(); // 上传取消信号

  // 状态轮询相关
  private checkTimer: Map<string, NodeJS.Timeout> = new Map();

  /**
   * 私有构造函数，确保单例
   */
  private constructor() { }

  /**
   * 获取单例实例
   */
  public static getInstance(): FileImportService {
    if (!FileImportService.instance) {
      FileImportService.instance = new FileImportService();
    }
    return FileImportService.instance;
  }

  /**
   * 初始化文件导入服务
   */
  public async init() {
    await loadFFmpeg();
  }

  /**
   * 获取文件记录列表
   */
  public async getFileRecords() {
    const records = await getFileRecords();
    const existingRecords = fileImportStore.getState().fileRecords;
    const existingIds = new Set(existingRecords.map(r => r.id));
    
    records.forEach((r) => {
      // 只添加不存在的记录，避免重复
      if (!existingIds.has(r.id)) {
        fileImportStore.getState().addUploadRecords(r);
        if (r.status === ImportStatus.WAITING || r.status === ImportStatus.TRANSING) {
          this.startCheckRecord(r.id);
        }
      }
    });
  }

  /**
   * 获取上传收益
   */
  public async getUploadBenefits(needUpload: boolean): Promise<boolean> {
    try {
      const res = await getBenefits();
      fileImportStore.getState().setBenfit(res.benefit || DEFAULT_BENEFIT,  res.benefitUsed);
      if (needUpload) {
        fridayReporter.pushData({
          eventCode: 'HomePage_import',
        });
      }
      return true;
    } catch (e) {
      console.log('getUploadBenefits error >>> ', e);
      return false;
    }
  }

  /**
   * 插入文件到上传队列
   * @param files 文件列表
   */
  public filesImport(files: IFile[]) {
    for (let i = 0; i < files.length; i++) {
      fileImportStore.getState().addUploadRecords({
        id: files[i].uid,
        fileName: files[i].file.name,
        fileType: FileType.AUDIO,
        status: ImportStatus.WAIT_UPLOAD,
        remainTime: undefined,
        uploadProcess: undefined,
        fileKey: undefined,
        fileSize: undefined
      });
      this.uploadFiles.push(files[i]);
    }
    this.addUploadTask();
  }


  /**
   * 删除记录
   */
  public async deleteRecord(id: string) {
    const record = fileImportStore.getState().fileRecords.find(e => e.id === id);
    if (!record) {
      console.log('deleteRecord id 找不到对应记录，退出');
      return;
    }
    if (record.status === ImportStatus.UPLOADING) {
      console.log(`取消id=${id}的上传`);
      this.signals.get(id)?.abort(); // 取消上传
      this.currUploadIndex--;
    } else if (record.status === ImportStatus.WAITING || record.status === ImportStatus.TRANSING) {
      console.log(`调用取消转写接口，id=${id}`);
      const res = await cancelTransform(id);
      if (!res) {
        message.warning('取消转写失败');
        return;
      }
    } else if (record.status === ImportStatus.TRANS_ERROR) {
      const res = await cancelTransform(id);
      if (!res) {
        message.warning('取消转写失败');
        return;
      }
    }
    // 等待上传或正在上传的，删除掉uploadFiles的文件
    if (record.status < ImportStatus.WAITING) {
      this.deleteFileById(id, false);
    }
    fileImportStore.getState().removeUploadRecord(id);
  }

  /**
   * 重试操作
   */
  public async retry(id: string) {
    const record = fileImportStore.getState().fileRecords.find(e => e.id === id);
    if (!record) {
      console.log('retry id 找不到对应记录，退出');
      return;
    }

    switch (record.status) {
      case ImportStatus.TRANS_ERROR: {
        const res = await retryTransform(id);
        if (res) {
          console.log('调用重试接口成功，开始检测');
          this.updateRecord(id, { status: ImportStatus.TRANSING });
          this.startCheckRecord(id);
        } else {
          message.warning('重试失败，请稍后再试');
          console.log('调用重试接口失败');
        }
        break;
      }
      case ImportStatus.UPLOAD_ERROR: {
        const currFile = this.uploadFiles.find(f => f.uid === id);
        if (currFile) {
          this.updateRecord(id, { status: ImportStatus.WAIT_UPLOAD });
          this.startUpload(currFile, currFile.uid);
        } else {
          message.warning('重试失败，请稍后再试');
        }
        break;
      }
      case ImportStatus.ADD_ERROR: {
        const currFile = this.uploadFiles.find(f => f.uid === id);
        if (currFile) {
          this.updateRecord(id, { status: ImportStatus.UPLOADING });
          this.addRecord(currFile.file, currFile.duration, record.fileKey!, record.fileSize!, id);
        } else {
          message.warning('重试失败，请稍后再试');
          console.log('重新新增失败');
        }
        break;
      }
    }
  }

  /**
   * 重置服务状态
   */
  public reset() {
    this.signals.forEach((signal) => {
      signal.abort();
    });
    this.checkTimer.forEach((t) => {
      clearTimeout(t);
    });
    this.checkTimer.clear();
    fileImportStore.getState().resetStore();
  }

  /**
   * 检查是否可以退出
   */
  public checkCanExit() {
    const records = fileImportStore.getState().fileRecords;
    let canExit = true;
    for (let i = 0; i < records.length; i++) {
      // 如果存在等待上传、上传中或等待转写的记录，则不能退出
      if (records[i].status <= ImportStatus.WAITING) {
        canExit = false;
        break;
      }
    }
    return canExit;
  }

  /**
   * 更新记录
   */
  private updateRecord = (
    id: string,
    newRecord: {
      newId?: string;
      status?: number;
      remainTime?: number;
      uploadProcess?: number;
      fileKey?: string;
      fileSize?: number
    },
  ) => {
    fileImportStore.getState().updateSingleRecord(id, newRecord);
  }

  /**
   * 检测并添加上传任务
   */
  private addUploadTask() {
    console.log('addUploadTask', this.currUploadIndex, this.uploadFiles.length);
    if (this.currUploadIndex >= this.uploadFiles.length) {
      console.log('文件已经全部上传完毕，返回');
      return;
    }
    for (let i = this.currUploadIndex + 1; i < this.uploadFiles.length; i++) {
      if (this.uploadingCount >= MAX_UPLOAD_COUNT) {
        console.log('正在上传的数量达到上限：', this.uploadingCount);
        break;
      } else {
        this.uploadingCount = this.uploadingCount + 1;
        this.currUploadIndex = i;
        this.startUpload(this.uploadFiles[i], this.uploadFiles[i].uid);
      }
    }
  }

  /**
   * 开始上传文件：md5->获取策略->上传到cstore
   */
  private async startUpload(fileItem: IFile, id: string) {
    let res: ICstoreUploadRes | undefined = undefined;
    try {
      const md5 = await getFileMD5(fileItem.file);
      if (this.uploadFiles.find(f => f.uid === id) === undefined) {
        console.log('md5生成成功，但是列表已经找不到这个文件，说明取消任务，返回');
        this.uploadingCount--;
        this.currUploadIndex--;
        this.addUploadTask();
        return;
      }
      this.updateRecord(id, { status: ImportStatus.UPLOADING, uploadProcess: 0 });
      console.log(`开始获取上传协议 ${fileItem.file.name} md5=${md5}`);
      const data = await getUploadPolicy(md5, '.' + fileItem.file.name.split('.').pop());

      const formData = new FormData();
      data.formFields.forEach(filed => {
        formData.append(filed.name, filed.value);
      });
      formData.append('file', fileItem.file);

      console.log(`开始上传文件 ${fileItem.file.name} md5=${md5}`);
      // 创建一个新的 AbortController 实例
      const controller = new AbortController();
      const { signal } = controller;
      this.signals.set(id, controller);
      res = await uploadToCstore(data.uploadUrl, formData, signal,
        (progress) => {
          this.updateRecord(id, { uploadProcess: progress });
        });
      this.signals.delete(id); // 上传成功删除控制器
      console.log('文件上传成功', res);
    } catch (e: any) {
      if (e.errCode === "ERR_CANCELED") {
        console.log("请求已取消");
        this.uploadingCount--;
        this.addUploadTask();
        return;
      }
      this.updateRecord(id, { status: ImportStatus.UPLOAD_ERROR });
      console.log('上传失败', e);
    }

    this.uploadingCount--;
    this.addUploadTask();
    if (res) this.addRecord(fileItem.file, fileItem.duration, res.fileKey, res.fileSize, id);
  }

  /**
   * 新增导入记录
   */
  private async addRecord(file: File, duration: number, fileKey: string, fileSize: number, id: string) {
    this.updateRecord(id, { fileKey, fileSize });
    try {
      console.log(`调用新增记录接口 ${file.name} duration=${duration}`);
      const addRecordRes = await addFileToTransform(file.name, fileKey, fileSize, duration);
      console.log(`新增转写记录成功，转写id=${addRecordRes}`);
      this.deleteFileById(id, true);
      this.updateRecord(id, { newId: addRecordRes });
      this.startCheckRecord(addRecordRes);
    } catch (e) {
      this.updateRecord(id, { status: ImportStatus.ADD_ERROR });
    }
  }

  /**
   * 定时检测记录的状态
   */
  public async startCheckRecord(id: string) {
    try {
      console.log('循环检测记录状态，记录id =', id);

      if (fileImportStore.getState().fileRecords.find(record => record.id === id) === undefined) {
        console.log('stroe已经找不到对应id，是被删除了，终止循环');
        return;
      }
      if (id != "") {
        const res = await getSingleRecord(id);
        if (res) {
          this.updateRecord(id, { status: res.status, remainTime: res.remainTime });
          if (res.status === ImportStatus.TRANS_SUCCESS) {
            console.log('转写成功');
            // 转写成功时，benefitUsed 本地 +1，然后调用 getUploadBenefits 更新
            const currentBenefitUsed = fileImportStore.getState().benefitUsed;
            fileImportStore.getState().setBenfit(
              fileImportStore.getState().benefit,
              currentBenefitUsed + 1
            );
            // 调用接口更新权益信息
            this.getUploadBenefits(false);
            eventBus.emit('refresh', res.fileName); // 转写成功，发出通知
            // 转写成功后，从 fileRecords 中删除该记录
            fileImportStore.getState().removeUploadRecord(id);
            this.checkTimer.delete(id);
            return;
          }
          if (res.status === ImportStatus.TRANS_ERROR) {
            console.log('转写错误');
            // 调用接口更新权益信息
            this.getUploadBenefits(false);
            this.checkTimer.delete(id);
            return;
          }
          const timer = setTimeout(() => {
            this.startCheckRecord(id);
          }, POLLING_INTERVAL);

          this.checkTimer.set(id, timer);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('startCheckRecord error', error);
      if (error?.errCode === 'ERR_NETWORK') {
        // 断网
        this.checkTimer.delete(id);
        const timer = setTimeout(() => {
          this.startCheckRecord(id);
        }, POLLING_INTERVAL);
        this.checkTimer.set(id, timer);
      }
    }
  }

  /**
   * 根据ID删除文件
   */
  private deleteFileById(uid: string, reduceIndex: boolean) {
    console.log(`删除文件，uid=${uid}`);
    const file = this.uploadFiles.find(f => f.uid === uid);
    console.log(file);
    if (file) {
      const idx = this.uploadFiles.indexOf(file);
      this.uploadFiles.splice(idx, 1);
      reduceIndex && this.currUploadIndex--;
    }
  }
}

export default FileImportService;
